import { useState } from 'react';

import { Plus, Shield, ShieldOff } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

import { useSuspensoesDoCredenciado } from './detalheDados';

interface AbaSuspensoesProps {
  credenciadoId: string;
  statusDoCredenciado: string;
}

const MOTIVOS = [
  { valor: 'INVESTIGACAO_OCORRENCIA', rotulo: 'Investigação de ocorrência grave' },
  { valor: 'DOC_VENCIDA', rotulo: 'Documentação vencida' },
  { valor: 'PROCESSO_SEI', rotulo: 'Processo SEI em curso' },
  { valor: 'LIMINAR_JUDICIAL', rotulo: 'Liminar judicial' },
  { valor: 'INADIMPLENCIA', rotulo: 'Inadimplência' },
  { valor: 'SOLICITACAO_CREDENCIADO', rotulo: 'Pedido do próprio credenciado' },
  { valor: 'OUTRO', rotulo: 'Outro' },
];

/**
 * Aba "Suspensões" — equivalente ao `SuspensaoCautelarPanel` do protótipo.
 * Aplicar e levantar suspensão eram RPCs que mudavam `credenciados.status` de
 * verdade; sem escrita em tabela cadastral (CLAUDE.md, "Regras de banco"), as
 * duas ações mostram o aviso de indisponibilidade em vez de fingir aplicar.
 */
export function AbaSuspensoes({ credenciadoId, statusDoCredenciado }: AbaSuspensoesProps) {
  const { data: lista, isLoading } = useSuspensoesDoCredenciado(credenciadoId, statusDoCredenciado);
  const [aplicarAberto, setAplicarAberto] = useState(false);
  const [levantarAberto, setLevantarAberto] = useState(false);

  const ativa = lista?.find((s) => s.status === 'ATIVA');

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" aria-hidden="true" /> Suspensões cautelares
          {ativa && (
            <Badge variant="destructive" className="ml-2">
              ATIVA
            </Badge>
          )}
        </CardTitle>
        {!ativa && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAplicarAberto(true)}
            className="gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Aplicar suspensão
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <Spinner size="sm" />
        ) : !lista || lista.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="Nenhuma suspensão registrada"
            description="O credenciado nunca foi suspenso cautelarmente."
          />
        ) : (
          <div className="space-y-2">
            {lista.map((suspensao) => (
              <div key={suspensao.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={suspensao.status === 'ATIVA' ? 'destructive' : 'secondary'}>
                      {suspensao.status}
                    </Badge>
                    <span className="text-sm font-medium">
                      {MOTIVOS.find((m) => m.valor === suspensao.motivo)?.rotulo ??
                        suspensao.motivo}
                    </span>
                  </div>
                  <span className="text-muted-foreground text-xs">
                    {formatarData(suspensao.data_inicio)}
                    {suspensao.data_fim_prevista
                      ? ` → ${formatarData(suspensao.data_fim_prevista)}`
                      : ''}
                  </span>
                </div>
                {suspensao.motivo_detalhe && (
                  <p className="text-muted-foreground mt-1 text-xs">{suspensao.motivo_detalhe}</p>
                )}
                {suspensao.processo_sei && (
                  <p className="mt-1 text-xs">
                    SEI: <code className="font-mono">{suspensao.processo_sei}</code>
                  </p>
                )}
                {suspensao.status === 'ATIVA' && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => setLevantarAberto(true)}
                    >
                      <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" /> Levantar suspensão
                    </Button>
                  </div>
                )}
                {suspensao.levantamento_motivo && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    Levantamento: {suspensao.levantamento_motivo}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AplicarSuspensaoDialog
        open={aplicarAberto}
        onOpenChange={setAplicarAberto}
        onConfirmar={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
      />

      <ResponsiveModal
        open={levantarAberto}
        onOpenChange={setLevantarAberto}
        title="Levantar suspensão cautelar"
        description="O credenciado voltaria a status ATIVO se não houvesse outra suspensão ou processo de descredenciamento."
        footer={
          <>
            <Button variant="outline" onClick={() => setLevantarAberto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
                setLevantarAberto(false);
              }}
            >
              Levantar
            </Button>
          </>
        }
      >
        <div>
          <Label>Motivo do levantamento</Label>
          <Textarea className="mt-1.5" rows={3} placeholder="Descreva o motivo do levantamento…" />
        </div>
      </ResponsiveModal>
    </Card>
  );
}

function AplicarSuspensaoDialog({
  open,
  onOpenChange,
  onConfirmar,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmar: () => void;
}) {
  const [motivo, setMotivo] = useState('INVESTIGACAO_OCORRENCIA');
  const [detalhe, setDetalhe] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [sei, setSei] = useState('');
  const [bloqueiaOs, setBloqueiaOs] = useState(true);
  const [bloqueiaPagamentos, setBloqueiaPagamentos] = useState(false);
  const [bloqueiaCredenciamento, setBloqueiaCredenciamento] = useState(true);
  const [observacoes, setObservacoes] = useState('');

  const confirmar = () => {
    onConfirmar();
    onOpenChange(false);
    setDetalhe('');
    setDataFim('');
    setSei('');
    setObservacoes('');
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Aplicar suspensão cautelar"
      description="O credenciado passaria a status SUSPENSO durante a vigência. Efeitos configuráveis abaixo."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmar}>Aplicar suspensão</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>Motivo</Label>
          <Select value={motivo} onValueChange={setMotivo}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOTIVOS.map((m) => (
                <SelectItem key={m.valor} value={m.valor}>
                  {m.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Detalhe do motivo</Label>
          <Textarea
            className="mt-1.5"
            value={detalhe}
            onChange={(evento) => setDetalhe(evento.target.value)}
            rows={2}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Data fim prevista (opcional)</Label>
            <Input
              className="mt-1.5"
              type="date"
              value={dataFim}
              onChange={(evento) => setDataFim(evento.target.value)}
            />
          </div>
          <div>
            <Label>Processo SEI</Label>
            <Input
              className="mt-1.5"
              value={sei}
              onChange={(evento) => setSei(evento.target.value)}
              placeholder="0000.0000/0000000-0"
            />
          </div>
        </div>
        <div className="space-y-2 rounded-md border p-3">
          <p className="text-muted-foreground text-xs font-medium">Efeitos da suspensão</p>
          <div className="flex items-center justify-between">
            <Label className="font-normal normal-case tracking-normal">Bloqueia novas OS</Label>
            <Switch checked={bloqueiaOs} onCheckedChange={setBloqueiaOs} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="font-normal normal-case tracking-normal">Bloqueia pagamentos</Label>
            <Switch checked={bloqueiaPagamentos} onCheckedChange={setBloqueiaPagamentos} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="font-normal normal-case tracking-normal">
              Bloqueia novo credenciamento
            </Label>
            <Switch checked={bloqueiaCredenciamento} onCheckedChange={setBloqueiaCredenciamento} />
          </div>
        </div>
        <div>
          <Label>Observações</Label>
          <Textarea
            className="mt-1.5"
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
            rows={2}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
